import storeModule from './storeModule';
import store from '@/store'
import {initialAbility} from '@/libs/acl/config';
import ability from '@/libs/acl/ability';
import {canNavigate, isRouteBlockedByPlan, isRouteBlockedByPlanAsync} from '@/libs/acl/routeProtection'
import {baseRoute} from '@/velta/utils/baseRoute';
import Vue from 'vue';
import router from '@/router';

// Importação dinâmica para evitar dependência circular
const getPlanPermissionsModule = () => import('@/libs/acl/planPermissions');

const showPlanBlockSwal = (planBlockInfo) => {
    Vue.prototype.$swal({
        title: 'Funcionalidade não disponível',
        html: `
            <div class="text-center">
                <p class="mb-1">A funcionalidade <strong>"${planBlockInfo.resourceName}"</strong> não está disponível no seu plano atual.</p>
                <p class="text-muted mb-0">Faça upgrade para o plano <strong>${planBlockInfo.requiredPlan}</strong> ou superior para desbloquear este recurso.</p>
            </div>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Ver Planos',
        cancelButtonText: 'Fechar',
        customClass: {
            confirmButton: 'btn btn-primary mr-2',
            cancelButton: 'btn btn-outline-secondary',
        },
        buttonsStyling: false,
    }).then((result) => {
        if (result.isConfirmed) {
            router.push({ name: 'planosepagamentos' })
        }
    })
}

const validateAuthRoute = async (to, from, next) => {
    const base = baseRoute();

    if (['agendamentoDetalheChecklist', 'agendamentoDetalhe', 'orcamentoChecklist'].includes(to.name) || (['agendamentoDetalheChecklist', 'agendamentoDetalhe', 'orcamentoChecklist'].includes(from.name) && base === 'cliente')) {
        await store.dispatch('auth/fetchAuthUserCliente')
        return next()
    }

    if (['site-home', 'catalogo-virtual', 'catalogo-virtual-agendamento', 'site-home-lavarapido', 'site-home-lavajato', 'site-home-esteticaautomotiva', 'site-home-mecanica', 'site-home-chapeacao', 'site-home-funilaria', 'site-home-borracharia'].indexOf(to.name) !== -1) {
        return next()
    }

    if (['login-client', 'login-company', 'esqueciminhasenha-company', 'esqueciminhasenha-client', 'cadastrolead', 'cadastroempresa', 'buscador', 'termodeuso', 'politicaprivacidade', 'cadastro', 'excluir-conta', 'login-geral', 'blog', 'blog-details'].indexOf(to.name) !== -1) {
        ability.update(initialAbility);
        if (store.getters['auth/authenticated'] || (to.name === 'login-client' || to.name === 'login-company')) {
            switch (base) {
                case 'empresa':
                    await store.dispatch('auth/logout')
                    break;
                case 'cliente':
                    await store.dispatch('auth/logoutCliente')
                    break;
            }
        }
    } else if (!store.getters['auth/authenticated']) {
        switch (base) {
            case 'empresa':
                await store.dispatch('auth/fetchAuthUser')
                break;
            case 'cliente':
                await store.dispatch('auth/fetchAuthUserCliente')
                break;
        }

        if (!store.getters['auth/authenticated']) {
            if (base === 'cliente') {
                return next({
                    name: 'login-client',
                    query: {
                        r: to.fullPath
                    }
                })
            }
            return next({
                name: 'login-company',
                query: {
                    r: to.fullPath
                }
            })
        } else {
            ability.update(store.getters['auth/abilities'])
        }
    } else if (base === 'empresa') {
        // Usuário já autenticado - garante que o auto-refresh das permissões do plano está ativo
        // e atualiza se estiverem expiradas
        try {
            const planModule = await getPlanPermissionsModule()
            planModule.startAutoRefresh()
            if (planModule.arePlanPermissionsExpired()) {
                planModule.fetchPlanPermissionsFromBackend()
            }
        } catch (e) {
            // Ignora erro se módulo não carregar
        }

        const empresaData = store.getters['auth/empresaData']
        const tenantConfig = store.getters['auth/tenantConfig']
        const routeOnboarding = ['onboarding', 'initialConfiguration'].indexOf(to.name) !== -1

        // Verificar se o onboarding foi completado (novo campo)
        const onboardingCompleted = tenantConfig && tenantConfig.onboarding_completed

        // Verificar configurações iniciais antigas (fallback)
        const faltaFazerConfiguracoesIniciais = (!tenantConfig.conf_configuracoes || !tenantConfig.conf_servicos || !tenantConfig.conf_vagas)

        // Se o onboarding não foi completado, redirecionar para o onboarding
        if (!onboardingCompleted && faltaFazerConfiguracoesIniciais) {
            if (empresaData && !routeOnboarding) {
                return next({
                    name: 'onboarding'
                })
            }
        } else if (!onboardingCompleted && !faltaFazerConfiguracoesIniciais) {
            // Empresas antigas que já configuraram mas não têm o campo onboarding_completed
            // Verificar apenas o WhatsApp de suporte
            const routeConfigurationWhatsappSuporte = ['initialConfigurationWhatsappSuporte', 'onboarding'].indexOf(to.name) !== -1
            const possuiWhatsappDeSuporteConfigurado = empresaData && empresaData.whatsapp_suporte

            if (empresaData && !routeConfigurationWhatsappSuporte && !possuiWhatsappDeSuporteConfigurado) {
                return next({
                    name: 'initialConfigurationWhatsappSuporte'
                })
            }
        }
    }


    if (!canNavigate(to)) {
        // Primeiro verifica localmente se pode ser bloqueio de plano
        const planBlockInfoLocal = isRouteBlockedByPlan(to)

        if (planBlockInfoLocal) {
            // Tenta atualizar permissões e re-verificar em paralelo para não bloquear
            try {
                const [permissoesAtualizadas] = await Promise.all([
                    store.dispatch('auth/refreshPermissions'),
                    store.dispatch('auth/refreshPlanPermissions'),
                ])

                if (permissoesAtualizadas) {
                    ability.update(store.getters['auth/abilities'])

                    // Verifica novamente se pode navegar após atualização
                    if (canNavigate(to)) {
                        return next()
                    }
                }

                // Verifica novamente com a validação assíncrona (dupla checagem no backend)
                const planBlockInfoAsync = await isRouteBlockedByPlanAsync(to)

                if (!planBlockInfoAsync) {
                    // Não é bloqueio de plano, provavelmente é bloqueio de perfil
                    return next({
                        name: base === 'cliente' ? 'naoAutorizado-client' : 'naoAutorizado'
                    })
                }

                // Confirmado: é bloqueio de plano - mostra swal de upgrade
                showPlanBlockSwal(planBlockInfoAsync)
                return next(false)
            } catch (error) {
                console.error('Erro ao verificar permissões:', error)
                showPlanBlockSwal(planBlockInfoLocal)
                return next(false)
            }
        }

        // Não é bloqueio de plano, é bloqueio de perfil ou outra razão
        return next({
            name: base === 'cliente' ? 'naoAutorizado-client' : 'naoAutorizado'
        })
    }

    return next();
}

export { validateAuthRoute, storeModule };
